import React, { useState, useEffect, useRef, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import type { TransformedProduct } from "../../types/productApiTypes";
import type { RawAttribute } from "../../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";

interface ProductDescriptionsProps {
  product?: TransformedProduct;
  isLoading?: boolean;
}

type TabKey = "description" | "instructions" | "video";

const getVideoIdAndProvider = (
  url: string
): { id: string; provider: "youtube" | "vimeo" } | null => {
  if (!url) return null;
  // YouTube
  let match = url.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/
  );
  if (match && match[1]) {
    return { id: match[1], provider: "youtube" };
  }
  // Vimeo
  match = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/);
  if (match && match[1]) {
    return { id: match[1], provider: "vimeo" };
  }
  return null;
};

const ProductDescriptions: React.FC<ProductDescriptionsProps> = ({
  product,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("description");
  const videoRef = useRef<HTMLDivElement>(null);

  const hasDescription =
    product?.description && product.description.trim() !== "";
  const hasInstructions =
    product?.extensions?.productInstructions?.instructions &&
    product.extensions.productInstructions.instructions.trim() !== "";
  const videoUrl = product?.extensions?.productVideo?.videoUrl;
  const videoInfo = useMemo(
    () => (videoUrl ? getVideoIdAndProvider(videoUrl) : null),
    [videoUrl]
  );
  const hasVideo = !!videoInfo;

  const productAttributes =
    product?.attributes?.filter(
      (attr) => attr.terms && attr.terms.length > 0
    ) || [];
  const hasAttributes = productAttributes.length > 0;

  const availableTabs: {
    key: TabKey;
    label: string;
    contentAvailable: boolean;
  }[] = [];

  const descriptionTabContentAvailable = !!(hasDescription || hasAttributes);
  if (descriptionTabContentAvailable || isLoading) {
    availableTabs.push({
      key: "description",
      label: "Açıklamalar",
      contentAvailable: descriptionTabContentAvailable,
    });
  }

  if (hasInstructions || isLoading) {
    availableTabs.push({
      key: "instructions",
      label: "Kullanım",
      contentAvailable: !!hasInstructions,
    });
  }

  if (hasVideo || isLoading) {
    availableTabs.push({
      key: "video",
      label: "Ürün Videosu",
      contentAvailable: hasVideo,
    });
  }

  useEffect(() => {
    let player: Plyr | null = null;
    if (videoRef.current && hasVideo) {
      const playerOptions: Plyr.Options = {
        youtube: {
          origin: window.location.origin,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
        },
        vimeo: {
          origin: window.location.origin,
        },
      };

      player = new Plyr(videoRef.current, playerOptions);
    }
    // The cleanup function is crucial. It will now run while the div
    // is still in the DOM (just hidden with display: none), preventing the error.
    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [hasVideo]); // --- MODIFIED: This effect now only depends on `hasVideo`

  useEffect(() => {
    if (availableTabs.length > 0) {
      const currentActiveTabInfo = availableTabs.find(
        (tab) => tab.key === activeTab
      );
      if (!currentActiveTabInfo || !currentActiveTabInfo.contentAvailable) {
        const firstAvailableContentTab = availableTabs.find(
          (tab) => tab.contentAvailable
        );
        if (firstAvailableContentTab) {
          setActiveTab(firstAvailableContentTab.key);
        } else if (availableTabs.length > 0) {
          setActiveTab(availableTabs[0].key);
        }
      }
    }
  }, [product, isLoading, activeTab]);

  if (
    !isLoading &&
    availableTabs.filter((tab) => tab.contentAvailable).length === 0
  ) {
    return null;
  }

  return (
    <div className="product-descriptions__wrapper">
      <div className="product-descriptions">
        {availableTabs.length > 0 && (
          <>
            <div className="product-descriptions__tabs">
              {isLoading ? (
                <>
                  <Skeleton
                    width={100}
                    height={30}
                    style={{ marginRight: "10px" }}
                  />
                  <Skeleton width={100} height={30} />
                </>
              ) : (
                availableTabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`product-descriptions__tab-button ${
                      activeTab === tab.key ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                    disabled={!tab.contentAvailable && !isLoading}
                  >
                    {tab.label}
                  </button>
                ))
              )}
            </div>
            <div className="product-descriptions__content">
              {isLoading ? (
                <>
                  <Skeleton count={5} />
                  <Skeleton width="30%" style={{ marginTop: "1rem" }} />
                  <Skeleton count={2} />
                </>
              ) : (
                <>
                  {/* --- MODIFIED: Use conditional CSS `display` instead of conditional rendering --- */}
                  <div
                    className="product-descriptions__panel"
                    style={{
                      display:
                        activeTab === "description" &&
                        descriptionTabContentAvailable
                          ? "block"
                          : "none",
                    }}
                  >
                    {hasDescription && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: product!.description,
                        }}
                      />
                    )}
                    {hasAttributes && (
                      <div className="product-attributes">
                        <h3 className="product-attributes__title">
                          Ek Bilgiler
                        </h3>
                        {productAttributes.map((attribute: RawAttribute) => (
                          <div key={attribute.id} className="product-attribute">
                            <span className="product-attribute__name">
                              {attribute.name}:
                            </span>
                            <span className="product-attribute__terms">
                              {attribute.terms
                                .map((term) => term.name)
                                .join(", ")}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div
                    className="product-descriptions__panel"
                    style={{
                      display:
                        activeTab === "instructions" && hasInstructions
                          ? "block"
                          : "none",
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        product!.extensions?.productInstructions
                          ?.instructions || "",
                    }}
                  />

                  {/* --- MODIFIED: Always render the video container but hide it with CSS --- */}
                  {hasVideo && videoInfo && (
                    <div
                      className="product-descriptions__panel"
                      style={{
                        display: activeTab === "video" ? "block" : "none",
                      }}
                    >
                      <div className="product-video__player-wrapper">
                        <div
                          ref={videoRef}
                          data-plyr-provider={videoInfo.provider}
                          data-plyr-embed-id={videoInfo.id}
                        ></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductDescriptions;
